import {
  formatNumber,
  formatDifference,
  formatRank,
  formatPercentile,
  formatDateRanges,
} from './formatting';
import { getCrimeDisplayName } from '../constants/crimes';
import { getStateAbbreviation } from '../constants/locations';
import { sortData, getSortOptions } from './sorting';
import { weightedPercentages } from '../constants/crimes';
import { buildReportCardsExportRows } from './reportCardsExportHelpers';
import { getLocationFootnotesMap } from './locationFootnotes';

const SUP_UNICODE_DIGITS = {
  0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹',
};

/** Plain-text / CSV / Excel: Unicode superscript for footnote indices (e.g. 12 → ¹²). */
export function toUnicodeSuperscriptNumber(n) {
  return String(n)
    .split('')
    .map((d) => SUP_UNICODE_DIGITS[d] ?? d)
    .join('');
}

/** Strip simple HTML from API footnote strings for text exports. */
export function stripHtmlToPlainTextForExport(html) {
  if (html == null || html === '') return '';
  if (typeof document === 'undefined') {
    return String(html)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const d = document.createElement('div');
  d.innerHTML = String(html);
  return (d.textContent || d.innerText || '').replace(/\r\n/g, '\n').trim();
}

/**
 * TXT/CSV/Excel block: "Footnotes:" then each line `¹  text` (two spaces after superscript).
 */
export function formatFootnotesBlockForTextExport(footnotesMap) {
  if (!footnotesMap || !Object.keys(footnotesMap).length) return '';
  const entries = Object.entries(footnotesMap).sort((a, b) => {
    const na = parseInt(a[0].replace('footnote_', ''), 10);
    const nb = parseInt(b[0].replace('footnote_', ''), 10);
    return na - nb;
  });
  const lines = ['Footnotes:'];
  for (const [key, html] of entries) {
    const num = parseInt(key.replace('footnote_', ''), 10);
    const sup = toUnicodeSuperscriptNumber(num);
    const plain = stripHtmlToPlainTextForExport(html);
    const paras = plain.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    if (!paras.length) continue;
    lines.push(`${sup}  ${paras[0]}`);
    const cont = '    ';
    for (let i = 1; i < paras.length; i++) {
      lines.push(cont + paras[i]);
    }
  }
  return lines.join('\n');
}

// ─── helpers ────────────────────────────────────────────────────────────────

/** Second arg `isScore` matches formatNumber (rounded integers, no decimals). */
const n  = (v, isScore)          => formatNumber(v, isScore);
const d  = (v, pct=false, mult=false) => formatDifference(v, pct, mult);
const rk = (v)                   => formatRank(v);
const pc = (v)                   => formatPercentile(v);

/** Remove characters invalid in file paths; keep spaces and commas for readable names. */
function sanitizeExportFilename(name) {
  return String(name || '')
    .replace(/[/\\:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeStateInCommaName(name) {
  if (!name || typeof name !== 'string') return name;
  const idx = name.lastIndexOf(',');
  if (idx === -1) return name;
  const place = name.slice(0, idx).trim();
  const st = name.slice(idx + 1).trim();
  const abbr = getStateAbbreviation(st);
  if (abbr) return `${place}, ${abbr}`;
  return name;
}

/** City/county names use state abbreviations (e.g. Cleveland, OH). Used by CSV/meta and raster header text. */
export function formatExportLocationDisplay(ctx) {
  const { locationData, locationName } = ctx;
  const info = locationData?.info;
  const fallback = (locationName || '').trim();

  if (!info?.type) {
    return normalizeStateInCommaName(fallback) || fallback;
  }

  const type = (info.type || '').toLowerCase();
  if (type === 'city') {
    if (info.city && info.state) {
      return `${info.city}, ${getStateAbbreviation(info.state)}`;
    }
  }
  if (type === 'county' && info.county && info.state) {
    return buildLocTypeName({ county: info.county, state: info.state }, 'county');
  }

  return normalizeStateInCommaName(fallback) || info.name || fallback;
}

/** Join stacked <th> lines into one export column title (matches on-screen table headers). */
function stackedHeader(...lines) {
  return lines
    .map((s) => (s == null ? '' : String(s)).trim())
    .filter(Boolean)
    .join(' ');
}

const POPULATION_GROUPS_EXPORT = [
  { value: 'all', label: 'All' },
  { value: 'under10k', label: '>10K' },
  { value: '10kplus', label: '10K+' },
  { value: '25kplus', label: '25K+' },
  { value: '50kplus', label: '50K+' },
  { value: '100kplus', label: '100K+' },
  { value: '250kplus', label: '250K+' },
  { value: '500kplus', label: '500K+' },
  { value: '1mplus', label: '1M+' },
];
const METRO_POPULATION_GROUPS_EXPORT = [
  { value: 'all', label: 'All' },
  { value: 'under100k', label: '<100K' },
  { value: '100kplus', label: '100K+' },
  { value: '250kplus', label: '250K+' },
  { value: '500kplus', label: '500K+' },
  { value: '1mplus', label: '1M+' },
  { value: '2_5mplus', label: '2.5M+' },
  { value: '5mplus', label: '5M+' },
  { value: '10mplus', label: '10M+' },
];

function getPopulationGroupExportLabel(populationGroup, isMetro) {
  const groups = isMetro ? METRO_POPULATION_GROUPS_EXPORT : POPULATION_GROUPS_EXPORT;
  const g = groups.find((x) => x.value === populationGroup);
  return g ? g.label : 'All';
}

function getLocationTypeComparisonFlags(locationTypeData, locationData, locationType) {
  const locations = locationTypeData?.data?.locations || [];
  const isMetro = locationType === 'metro';
  const isCounty = locationType === 'county';
  const isCity = locationType === 'city';
  const isCountyOrCity = isCounty || isCity;
  const isOneCountyOrCity =
    locations[0]?.total_counties_state === 1 || locations[0]?.total_cities_state === 1;
  const isAllCountiesNational = isCounty && locationData?.info?.name === 'All Counties';
  const isAllCitiesNational = isCity && locationData?.info?.name === 'All Cities';
  const isAllNational = isAllCountiesNational || isAllCitiesNational;
  const selectedState = locationData?.info?.state;
  const dataState = locations[0]?.state;
  const dataHasMultipleStates =
    locations.length > 1 && locations.some((loc) => loc.state !== locations[0]?.state);
  const dataIsNational = isCountyOrCity && dataHasMultipleStates;
  const selectedIsNational = !selectedState;
  const bothAreState = selectedState && dataState && !dataIsNational;
  const bothAreNational = selectedIsNational && dataIsNational;
  const dataMatchesSelection =
    bothAreNational || bothAreState || selectedState === dataState;
  const showComparisonDropdown =
    isCountyOrCity && !isOneCountyOrCity && !isAllNational && dataMatchesSelection;
  return {
    locations,
    isCountyOrCity,
    isMetro,
    isOneCountyOrCity,
    isAllNational,
    dataIsNational,
    showComparisonDropdown,
  };
}

function getLocationTypeRateComparisonTopLabel(
  locationType,
  isMetro,
  isCountyOrCity,
  stateAbbr,
  rateViewMode,
) {
  const locationPrefix = locationType.charAt(0).toUpperCase() + locationType.slice(1);
  if (isMetro) {
    return rateViewMode?.rateType === 'metro' ? 'Metro' : 'National';
  }
  if (rateViewMode?.rateType === locationType) {
    return rateViewMode?.rateScope === 'state'
      ? `${stateAbbr} ${locationPrefix}`
      : `US ${locationPrefix}`;
  }
  return rateViewMode?.rateScope === 'state' ? `${stateAbbr} State` : 'US National';
}

function getCompositeFieldExport(compareMode, isCountyOrCity, baseField) {
  if (!isCountyOrCity) return baseField;
  return compareMode === 'state' ? `state_${baseField}` : `national_${baseField}`;
}

/** Plain-text line matching `.main-table-top` for location-name (non-historical) exports. */
function buildMainTableTopLocationName(
  ctx,
  effectiveTab,
  { locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr },
) {
  const { locationData, compareMode, selectedDateRange } = ctx;
  const current = locationData?.data?.current || {};
  const locLabel = locType.charAt(0).toUpperCase() + locType.slice(1);

  if (effectiveTab === 'total') {
    if (isNation) return 'Results for the entire nation';
    if (isCountyOrCity) {
      const total =
        compareMode === 'state'
          ? (locType === 'county'
              ? current.total_counties_state
              : current.total_cities_state) || current.total_locations
          : (locType === 'county'
              ? current.total_counties_national
              : current.total_cities_national) || current.total_locations;
      return `Total ${compareMode === 'state' ? stateAbbr : 'US'} ${locType === 'county' ? 'Counties' : 'Cities'}: ${n(total)}`;
    }
    return `Total ${locLabel}s: ${n(current.total_locations)}`;
  }

  if (effectiveTab === 'trend') {
    const dr = formatDateRanges(selectedDateRange);
    if (!dr) return String(selectedDateRange ?? '');
    return `${dr.currentRange}  compared to  ${dr.prevRange}`;
  }

  if (effectiveTab === 'rate') {
    if (isNation && isNCVS) {
      return `US Residents Age 12 or Older: ${n(current.person_population)}  |  US Households: ${n(current.household_population)}`;
    }
    const popLabel = isNation ? 'United States' : locLabel;
    return `${popLabel} Population: ${n(current.population)}`;
  }

  if (effectiveTab === 'score') {
    if (isNation) return null;
    const compositeScoreField = getCompositeFieldExport(compareMode, isCountyOrCity, 'composite_score');
    const compositeScoreRankField = getCompositeFieldExport(
      compareMode,
      isCountyOrCity,
      'composite_score_rank',
    );
    const score = current[compositeScoreField];
    const rank = current[compositeScoreRankField];
    const scopePrefix = isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : '';
    let line = `${scopePrefix ? `${scopePrefix} ` : ''}KRIME Score™ (Composite):`;
    if (score != null) line += ` ${formatNumber(score, true)}`;
    if (score != null && rank != null) {
      const rankPrefix = isCountyOrCity
        ? compareMode === 'state'
          ? `${stateAbbr} `
          : 'US '
        : '';
      line += ` (${rankPrefix}${locLabel} Rank: ${rk(rank)})`;
    }
    return line;
  }

  return null;
}

/** Plain-text line(s) matching `.main-table-top` for location-type list (non-historical) exports. */
function buildMainTableTopLocationType(
  ctx,
  tab,
  { locType, isCountyOrCity, stateAbbr },
) {
  const {
    locationData,
    locationTypeData,
    selectedCrime,
    selectedSource,
    selectedDateRange,
    compareMode,
    rateViewMode,
  } = ctx;
  const populationGroup = ctx.populationGroup ?? 'all';
  const crimePrefix = selectedCrime.toLowerCase();
  const crimeDisplay = getCrimeDisplayName(selectedCrime, selectedSource);
  const locationPrefix = locType.charAt(0).toUpperCase() + locType.slice(1);
  const flags = getLocationTypeComparisonFlags(locationTypeData, locationData, locType);
  const { locations, isMetro, showComparisonDropdown, isAllNational, isOneCountyOrCity, dataIsNational } =
    flags;
  const first = locations[0];
  if (!first && tab !== 'trend') return null;

  let stateAbbrRow = stateAbbr;
  if (isCountyOrCity && locations.length > 0) {
    stateAbbrRow = getStateAbbreviation(locations[0]?.state || '') || stateAbbr;
  }

  if (tab === 'total') {
    const avgField = isCountyOrCity
      ? compareMode === 'state'
        ? `${crimePrefix}_state_count_avg`
        : `${crimePrefix}_national_count_avg`
      : `${crimePrefix}_national_count_avg`;
    const avgVal = first?.[avgField];
    if (showComparisonDropdown) {
      return `${compareMode === 'state' ? stateAbbrRow : 'US'} ${locationPrefix} ${crimeDisplay} Average: ${n(avgVal)}`;
    }
    const usPrefix = isAllNational || isOneCountyOrCity || dataIsNational ? 'US ' : '';
    return `${usPrefix}${locationPrefix} ${crimeDisplay} Average: ${n(avgVal)}`;
  }

  if (tab === 'trend') {
    const dr = formatDateRanges(selectedDateRange);
    if (!dr) return String(selectedDateRange ?? '');
    return `${dr.currentRange}  compared to  ${dr.prevRange}`;
  }

  if (tab === 'rate') {
    const { avgF } = getRateFields(crimePrefix, isMetro, isCountyOrCity, compareMode, rateViewMode);
    const avgVal = first?.[avgF];
    const rateComp = getLocationTypeRateComparisonTopLabel(
      locType,
      isMetro,
      isCountyOrCity,
      stateAbbrRow,
      rateViewMode,
    );
    const popLabel = getPopulationGroupExportLabel(populationGroup, isMetro);
    if (isCountyOrCity || isMetro) {
      return `${rateComp} ${crimeDisplay} Rate: ${n(avgVal)}  |  Population: ${popLabel}`;
    }
    const natPrefix = isAllNational ? `US ${locationPrefix}` : 'National';
    return `${natPrefix} ${crimeDisplay} Rate: ${n(avgVal)}`;
  }

  if (tab === 'score') {
    const scoreTopCrimeLabel =
      selectedCrime === 'all_crime' && selectedSource !== 'BJS_NCVS'
        ? 'All Crime'
        : getCrimeDisplayName(selectedCrime, selectedSource);
    const pct = weightedPercentages[selectedCrime] ?? '-';
    const asterisk = crimeTypesWithScoreWeightAsterisk(selectedCrime) ? '*' : '';
    if (showComparisonDropdown) {
      return `${compareMode === 'state' ? stateAbbrRow : 'US'} ${locationPrefix} ${scoreTopCrimeLabel} Score Weight: ${pct}${asterisk}`;
    }
    const usPrefix = isAllNational || isOneCountyOrCity || dataIsNational ? 'US ' : '';
    return `${usPrefix}${locationPrefix} ${scoreTopCrimeLabel} Score Weight: ${pct}${asterisk}`;
  }

  return null;
}

/** Match LocationTypeRatesTable population filter when exporting rates. */
function checkPopulationMatchExport(population, groupValue) {
  if (!population || isNaN(population)) return false;
  switch (groupValue) {
    case 'under10k':
      return population < 10000;
    case 'under100k':
      return population < 100000;
    case '10kplus':
      return population >= 10000;
    case '25kplus':
      return population >= 25000;
    case '50kplus':
      return population >= 50000;
    case '100kplus':
      return population >= 100000;
    case '250kplus':
      return population >= 250000;
    case '500kplus':
      return population >= 500000;
    case '1mplus':
      return population >= 1000000;
    case '2_5mplus':
      return population >= 2500000;
    case '5mplus':
      return population >= 5000000;
    case '10mplus':
      return population >= 10000000;
    default:
      return true;
  }
}

function exportTabToSortApiTab(tab) {
  const map = { total: 'totals', trend: 'trends', rate: 'rates', score: 'scores' };
  return map[tab] || tab;
}

function findSortOptionLabel(sortOptions, sortKey) {
  if (!sortKey) return '';
  let opt = sortOptions.find((o) => o.key === sortKey);
  if (opt) return opt.label;
  opt = sortOptions.find(
    (o) => sortKey === o.key || sortKey.startsWith(`${o.key}_`),
  );
  return opt?.label || sortKey;
}

function buildLocationTypeSortedByLine(
  ctx,
  sortApiTab,
  locType,
  isCountyOrCity,
  stateAbbr,
  sortConfig,
) {
  const { selectedCrime, compareMode, rateViewMode } = ctx;
  const crimePrefix = selectedCrime.toLowerCase();
  const locationPrefix = locType.charAt(0).toUpperCase() + locType.slice(1);
  const isMetroLt = locType === 'metro';
  const compareArgForSort =
    sortApiTab === 'rates' && (isCountyOrCity || isMetroLt) ? null : compareMode;
  const sortOptions = getSortOptions(
    sortApiTab,
    locationPrefix,
    crimePrefix,
    selectedCrime,
    compareArgForSort,
    isCountyOrCity,
    stateAbbr,
    rateViewMode,
    ctx.selectedSource,
  );
  const label = findSortOptionLabel(sortOptions, sortConfig?.key);
  const dir = sortConfig?.direction === 'asc' ? 'ASC' : 'DESC';
  return `Sorted By: ${label} (${dir})`;
}

function buildPaginationResultsLine(totalItems, currentPage, itemsPerPage) {
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(startItem + itemsPerPage - 1, totalItems);
  const noun = totalItems === 1 ? 'result' : 'results';
  return `${startItem} to ${endItem} of ${formatNumber(totalItems)} ${noun}`;
}

/** Same inputs as {@link buildPaginationResultsLine}; used for raster PDF pagination (bold total count). */
export function getPaginationLineParts(totalItems, currentPage, itemsPerPage) {
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(startItem + itemsPerPage - 1, totalItems);
  const noun = totalItems === 1 ? 'result' : 'results';
  return { startItem, endItem, totalFormatted: formatNumber(totalItems), noun };
}

function getRatesComparisonLabelFromCompareMode(
  compareMode,
  isMetro,
  isCountyOrCity,
  stateAbbr,
  rateViewMode,
  locLabel,
) {
  return getRateCompLabel(isMetro, isCountyOrCity, compareMode, rateViewMode, stateAbbr, locLabel);
}

function getLocationTypeRateComparisonLabel(locType, isMetro, isCountyOrCity, stateAbbr, rateViewMode) {
  const locationPrefix = locType.charAt(0).toUpperCase() + locType.slice(1);
  if (isMetro) {
    return rateViewMode?.rateType === 'metro' ? 'Metro' : 'National';
  }
  if (rateViewMode?.rateType === locType) {
    return rateViewMode?.rateScope === 'state' ? `${stateAbbr} ${locationPrefix}` : `US ${locationPrefix}`;
  }
  return rateViewMode?.rateScope === 'state' ? `${stateAbbr} State` : 'US National';
}

const TAB_SINGULAR = {
  totals: 'Total', trends: 'Trend', rates: 'Rate', scores: 'Score', reports: 'Report',
};

/** Maps analysis tab keys to singular tokens used by export builders (trim + case-safe). */
function normalizeExportTab(activeTab) {
  const key = String(activeTab ?? '').trim().toLowerCase();
  const singular = TAB_SINGULAR[key];
  if (singular) return singular.toLowerCase();
  return key;
}

const crimeRows = (isNCVS) => [
  { crimeType: 'All Crime',           prefix: 'all_crime',           level: 0 },
  { crimeType: 'Violent Crime',       prefix: 'violent_crime',       level: 1 },
  { crimeType: 'Murder',              prefix: 'murder',              level: 2 },
  { crimeType: 'Rape',                prefix: 'rape',                level: 2 },
  { crimeType: 'Robbery',             prefix: 'robbery',             level: 2 },
  { crimeType: 'Aggravated Assault',  prefix: 'aggravated_assault',  level: 2 },
  ...(isNCVS ? [{ crimeType: 'Simple Assault', prefix: 'simple_assault', level: 2 }] : []),
  { crimeType: 'Property Crime',      prefix: 'property_crime',      level: 1 },
  { crimeType: 'Burglary',            prefix: 'burglary',            level: 2 },
  ...(isNCVS ? [{ crimeType: 'Trespassing', prefix: 'trespassing', level: 2 }] : []),
  { crimeType: 'Larceny/Theft',       prefix: 'larceny_theft',       level: 2 },
  { crimeType: 'Motor Vehicle Theft', prefix: 'motor_vehicle_theft', level: 2 },
];

const buildLocTypeName = (location, locationType) => {
  const isCounty = locationType === 'county';
  const isCity   = locationType === 'city';
  if (isCounty) {
    const abbr = getStateAbbreviation(location.state || '');
    if (location.county === 'Carson City' && location.state === 'Nevada')
      return `${location.county}, ${abbr}`;
    if (location.state === 'Louisiana')
      return `${location.county} Parish, ${abbr}`;
    if (location.state === 'Alaska')
      return `${location.county} Borough, ${abbr}`;
    return `${location.county} County, ${abbr}`;
  }
  if (isCity) return `${location.city}, ${getStateAbbreviation(location.state || '')}`;
  return location.name || '';
};

// ─── filename / title builder ─────────────────────────────────────────────────
function buildMeta(ctx) {
  const {
    view, activeTab, isHistorical,
    selectedCrime, selectedDateRange,
  } = ctx;

  /** Report cards are always current-year snapshots; never label exports as historical. */
  const effectiveHistorical = activeTab === 'reports' ? false : isHistorical;

  const isLocType = view === 'locationType';
  const tabLabel = TAB_SINGULAR[activeTab] || (activeTab.charAt(0).toUpperCase() + activeTab.slice(1));
  const crimeLabel = selectedCrime === 'all_crime'
    ? 'All Crime'
    : getCrimeDisplayName(selectedCrime);

  const locationDisplay = formatExportLocationDisplay(ctx);
  const useSpecificCrime = effectiveHistorical || isLocType;
  /** Title line: use "Crime" for all_crime (including scores) in historical and location-type exports. */
  const crimeSegmentForTitle = useSpecificCrime
    ? (selectedCrime === 'all_crime' ? 'Crime' : crimeLabel)
    : 'Crime';

  let title;
  if (effectiveHistorical) {
    title = `KRIME ${tabLabel} - ${locationDisplay} Historical ${crimeSegmentForTitle} Data`;
  } else if (isLocType) {
    title = `KRIME ${tabLabel} - ${locationDisplay} ${selectedDateRange} ${crimeSegmentForTitle} Data`;
  } else {
    title = `KRIME ${tabLabel} - ${locationDisplay} ${selectedDateRange} Crime Data`;
  }

  const filename = sanitizeExportFilename(title);
  return { filename, title, crimeLabel, tabLabel };
}

/**
 * Browser tab / share title aligned with export filenames (buildMeta).
 * Pass locationData with at least { info } for location display formatting.
 */
export function buildExportPageTitle({
  view = 'locationName',
  activeTab,
  isHistorical,
  selectedCrime,
  selectedDateRange,
  locationData,
  locationName,
}) {
  return buildMeta({
    view,
    activeTab,
    isHistorical,
    selectedCrime,
    selectedDateRange,
    locationData,
    locationName,
  }).title;
}

// ─── main export ─────────────────────────────────────────────────────────────

/**
 * Returns { title, filename, headers, rows, mainTableTopHeading }
 *
 * mainTableTopHeading — plain text matching `.main-table-top` (null for historical time-series tables, reports, and empty extracts).
 * sortedByLine — location-type only: `Sorted By: {label} (ASC|DESC)` aligned with TableHeader / getSortOptions.
 * paginationSummary — location-type only: same wording as Pagination (`X to Y of Z results`).
 * paginationLineParts — location-type only: structured line for raster PDF (bold total count).
 * scoreFootnote — scores tab: methodology line when the UI shows * on weight(s); CSV/TXT/XLSX and raster PDF/PNG/print, below pagination (if any) and location footnotes (if any), above source.
 * locationFootnotes — map like FootnotesModal (`footnote_1`…), or null; text exports list footnotes before methodology; raster draws footnotes above methodology and source.
 *
 * ctx shape:
 *   view, activeTab, isHistorical, selectedView,
 *   locationData      { info, data: { current, historical? } }
 *   locationTypeData  { data: { locations, ... }, type }
 *   selectedCrime, selectedDateRange, selectedSource,
 *   compareMode, rateViewMode,
 *   populationGroup   (optional; location-type rates population filter)
 *   currentPage, itemsPerPage,
 *   sortConfig,
 *   locationName  (formatted display string)
 */
/** Same body text as ScoreMethodologyModal footnote (asterisk line). */
export const SCORE_METHODOLOGY_FOOTNOTE =
  '* Composite KRIME Scores™ are calculated from weighted individual crimes only (Murder, Rape, Robbery, Aggravated Assault, Burglary, Larceny/Theft, Motor Vehicle Theft). Aggregate weights represent the sum of individual crime weights within each category (All Crime, Violent Crime, Property Crime).';

function crimeTypesWithScoreWeightAsterisk(selectedCrime) {
  return selectedCrime === 'all_crime' || selectedCrime === 'violent_crime' || selectedCrime === 'property_crime';
}

/** Footnote for CSV/TXT/XLSX when the scores UI shows an asterisk on weight(s). */
function getScoreMethodologyFootnoteForExport(ctx) {
  const tab = normalizeExportTab(ctx.activeTab);
  if (tab !== 'score') return null;
  if (ctx.view === 'locationName') return SCORE_METHODOLOGY_FOOTNOTE;
  if (crimeTypesWithScoreWeightAsterisk(ctx.selectedCrime)) return SCORE_METHODOLOGY_FOOTNOTE;
  return null;
}

function withExportMetaDefaults(result, ctx) {
  return {
    ...result,
    sortedByLine: result.sortedByLine ?? null,
    paginationSummary: result.paginationSummary ?? null,
    paginationLineParts: result.paginationLineParts ?? null,
    scoreFootnote: getScoreMethodologyFootnoteForExport(ctx),
    locationFootnotes: getLocationFootnotesMap(ctx.locationData),
  };
}

export function extractTableData(ctx) {
  const {
    view, activeTab, isHistorical,
    locationData, selectedSource,
    currentPage, itemsPerPage, sortConfig,
  } = ctx;

  const { filename, title, crimeLabel } = buildMeta(ctx);

  const info         = locationData?.info || {};
  const locType      = (info.type || '').toLowerCase();
  const isNation     = locType === 'nation';
  const isCounty     = locType === 'county';
  const isCity       = locType === 'city';
  const isMetro      = locType === 'metro';
  const isCountyOrCity = isCounty || isCity;
  const isNCVS       = selectedSource === 'BJS_NCVS';

  let stateAbbr = '';
  if (isCountyOrCity) {
    if (info.name?.includes(',')) {
      stateAbbr = getStateAbbreviation(info.name.split(',')[1]?.trim());
    } else if (info.state) {
      stateAbbr = getStateAbbreviation(info.state);
    }
  }

  // ── Reports tab: Report cards (before historical so toggled historical never exports time series)
  if (activeTab === 'reports') {
    return withExportMetaDefaults(
      buildReports(
        ctx, title, filename, crimeLabel,
        locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr,
      ),
      ctx,
    );
  }

  // ── Historical (locationName) time series
  if (isHistorical && view === 'locationName') {
    return withExportMetaDefaults(
      buildHistorical(
        ctx, title, filename,
        locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr,
      ),
      ctx,
    );
  }

  // ── Location Type (comparison list) view ──────────────────────────────────
  if (view === 'locationType') {
    return withExportMetaDefaults(
      buildLocationType(
        ctx, title, filename, crimeLabel,
        locType, isNation, isCountyOrCity, isNCVS, stateAbbr,
        currentPage, itemsPerPage, sortConfig,
      ),
      ctx,
    );
  }

  // ── Location Name (current year, single location) view ───────────────────
  return withExportMetaDefaults(
    buildLocationName(
      ctx, title, filename,
      locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr,
    ),
    ctx,
  );
}

// ─── builder: Reports summary (ReportCards layout — see reportCardsExportHelpers) ─
function buildReports(
  ctx, title, filename, _crimeLabel,
  locType, isNation, isCountyOrCity, isMetro, _isNCVS, stateAbbr,
) {
  const { compareMode, rateViewMode, view } = ctx;
  const { headers, rows } = buildReportCardsExportRows(ctx, {
    locType,
    isNation,
    isCountyOrCity,
    isMetro,
    stateAbbr,
    compareMode,
    rateViewMode,
    view,
  });
  return { title, filename, headers, rows, mainTableTopHeading: null };
}

// ─── builder: Historical ──────────────────────────────────────────────────────
function buildHistorical(
  ctx, title, filename,
  locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr,
) {
  const { locationData, selectedCrime, selectedSource, compareMode, rateViewMode } = ctx;
  const data    = locationData?.data;
  const rows    = [];
  const cp      = selectedCrime.toLowerCase();
  const locLabel = locType.charAt(0).toUpperCase() + locType.slice(1);
  const scopeLabel = isCountyOrCity
    ? (compareMode === 'state' ? stateAbbr : 'US')
    : '';
  const rawTab = normalizeExportTab(ctx.activeTab);
  const effectiveTab = rawTab === 'report' ? 'total' : rawTab;

  const historical = [...(data?.historical || [])]
    .sort((a, b) => b.year - a.year)
    .filter(y => y[`${cp}_count`] != null || y[`${cp}_rate`] != null || y[`${cp}_score`] != null);

  if (effectiveTab === 'total') {
    const headers = isNation
      ? ['Year', `Total ${getCrimeDisplayName(selectedCrime, selectedSource)}`]
      : [
        'Year',
        stackedHeader('Total', getCrimeDisplayName(selectedCrime, selectedSource)),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Average'),
        stackedHeader(`${scopeLabel} ${locLabel} Avg`, 'Difference'),
        stackedHeader(`% ${scopeLabel} ${locLabel} Avg`, 'Difference'),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Rank'),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Percentile'),
      ];
    historical.forEach(y => {
      const rankF = isCountyOrCity
        ? (compareMode === 'state' ? `${cp}_state_count_rank` : `${cp}_national_count_rank`)
        : `${cp}_count_rank`;
      const pctF = isCountyOrCity
        ? (compareMode === 'state' ? `${cp}_state_count_percentile` : `${cp}_national_count_percentile`)
        : `${cp}_count_percentile`;
      const avgV = compareMode === 'state' && isCountyOrCity
        ? y[`${cp}_state_count_avg`] : y[`${cp}_national_count_avg`];
      const dV   = compareMode === 'state' && isCountyOrCity
        ? y[`${cp}_state_count_avg_diff`] : y[`${cp}_national_count_avg_diff`];
      const dpV  = compareMode === 'state' && isCountyOrCity
        ? y[`${cp}_state_count_avg_diff_pct`] : y[`${cp}_national_count_avg_diff_pct`];
      const row  = [y.year, n(y[`${cp}_count`])];
      if (!isNation) row.push(n(avgV), d(dV), d(dpV, true), rk(y[rankF]), pc(y[pctF]));
      rows.push(row);
    });
    return { title, filename, headers, rows, mainTableTopHeading: null };
  }

  if (effectiveTab === 'trend') {
    const headers = [
      'Year',
      stackedHeader('Total', getCrimeDisplayName(selectedCrime, selectedSource)),
      stackedHeader('Previous', 'Total'),
      stackedHeader('Change from', 'Previous Total'),
      stackedHeader('% Change from', 'Previous Total'),
    ];
    if (!isNation) {
      headers.push(
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Rank'),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Percentile'),
      );
    }
    historical.forEach(y => {
      const rankF = isCountyOrCity
        ? (compareMode === 'state' ? `${cp}_state_trend_rank` : `${cp}_national_trend_rank`)
        : `${cp}_trend_rank`;
      const pctF = isCountyOrCity
        ? (compareMode === 'state' ? `${cp}_state_trend_percentile` : `${cp}_national_trend_percentile`)
        : `${cp}_trend_percentile`;
      const row = [
        y.year, n(y[`${cp}_count`]), n(y[`${cp}_count_prev`]),
        d(y[`${cp}_count_prev_diff`]), d(y[`${cp}_count_prev_diff_pct`], true),
      ];
      if (!isNation) row.push(rk(y[rankF]), pc(y[pctF]));
      rows.push(row);
    });
    return { title, filename, headers, rows, mainTableTopHeading: null };
  }

  if (effectiveTab === 'rate') {
    const { avgF, rankF, diffF, diffPctF, pctF } = getRateFields(
      cp, isMetro, isCountyOrCity, compareMode, rateViewMode,
    );
    const hComp = getRatesComparisonLabelFromCompareMode(
      compareMode, isMetro, isCountyOrCity, stateAbbr, rateViewMode, locLabel,
    );
    const rankHeaderBase = (isCountyOrCity || isMetro) ? hComp : locLabel;

    let headers;
    if (isNation && isNCVS) {
      headers = [
        'Year',
        stackedHeader('US Residents', 'Age 12 or Older'),
        stackedHeader('US', 'Households'),
        stackedHeader(getCrimeDisplayName(selectedCrime, selectedSource), 'Rate'),
      ];
    } else if (isNation) {
      headers = [
        'Year',
        stackedHeader('United States', 'Population'),
        `${getCrimeDisplayName(selectedCrime, selectedSource)} Rate`,
      ];
    } else {
      headers = [
        'Year',
        stackedHeader(locLabel, 'Population'),
        stackedHeader(getCrimeDisplayName(selectedCrime, selectedSource), 'Rate'),
        stackedHeader(hComp, 'Rate'),
        stackedHeader(`${hComp} Rate`, 'Difference'),
        stackedHeader(`% ${hComp} Rate`, 'Difference'),
        stackedHeader(rankHeaderBase, 'Rank'),
        stackedHeader(rankHeaderBase, 'Percentile'),
      ];
    }
    historical.forEach(y => {
      if (isNation && isNCVS) {
        rows.push([y.year, n(y.person_population), n(y.household_population), n(y[`${cp}_rate`])]);
      } else if (isNation) {
        rows.push([y.year, n(y.population), n(y[`${cp}_rate`])]);
      } else {
        rows.push([
          y.year, n(y.population), n(y[`${cp}_rate`]),
          n(y[avgF]), d(y[diffF], false, true), d(y[diffPctF], true),
          rk(y[rankF]), pc(y[pctF]),
        ]);
      }
    });
    return { title, filename, headers, rows, mainTableTopHeading: null };
  }

  if (effectiveTab === 'score') {
    const crimeDisplay = getCrimeDisplayName(selectedCrime, selectedSource);
    const scopeFirst = isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : '';
    const headers = [
      'Year',
      stackedHeader(`${scopeFirst} KRIME Score™`.trim(), '(Composite)'),
      stackedHeader(`${scopeFirst} ${locLabel} Rank`.trim(), '(Composite)'),
      stackedHeader(
        `${scopeFirst} ${selectedCrime === 'all_crime' ? 'All ' : ''}${crimeDisplay}`.replace(/\s+/g, ' ').trim(),
        'Score (Non-Weighted)',
      ),
      stackedHeader(`${scopeFirst} ${locLabel} Rank`.trim(), '(Non-Weighted)'),
    ];
    historical.forEach(y => {
      const scoreF = isCountyOrCity
        ? (compareMode === 'state' ? `${cp}_state_score` : `${cp}_national_score`)
        : `${cp}_score`;
      const rankF  = isCountyOrCity
        ? (compareMode === 'state' ? `${cp}_state_score_rank` : `${cp}_national_score_rank`)
        : `${cp}_score_rank`;
      const compScoreF = isCountyOrCity
        ? (compareMode === 'state' ? 'state_composite_score' : 'national_composite_score')
        : 'composite_score';
      const compRankF = isCountyOrCity
        ? (compareMode === 'state' ? 'state_composite_score_rank' : 'national_composite_score_rank')
        : 'composite_score_rank';
      rows.push([y.year, n(y[compScoreF], true), rk(y[compRankF]), n(y[scoreF], true), rk(y[rankF])]);
    });
    return { title, filename, headers, rows, mainTableTopHeading: null };
  }

  return { title, filename, headers: [], rows: [], mainTableTopHeading: null };
}

// ─── builder: LocationType ────────────────────────────────────────────────────
function buildLocationType(
  ctx, title, filename, crimeLabel,
  locType, isNation, isCountyOrCity, isNCVS, stateAbbr,
  currentPage, itemsPerPage, sortConfig,
) {
  const { locationTypeData, selectedCrime, compareMode, rateViewMode, selectedSource } = ctx;
  const crimeTableHeader = getCrimeDisplayName(selectedCrime, selectedSource);
  const locations   = locationTypeData?.data?.locations || [];
  const crimePrefix = selectedCrime.toLowerCase();
  const locLabel    = locType.charAt(0).toUpperCase() + locType.slice(1);
  const scopeLabel  = isCountyOrCity
    ? (compareMode === 'state' ? stateAbbr : 'US')
    : '';
  const tab = normalizeExportTab(ctx.activeTab);
  const sortApiTab = exportTabToSortApiTab(tab);
  const mainTableTopHeading = buildMainTableTopLocationType(ctx, tab, {
    locType, isCountyOrCity, stateAbbr,
  });

  // Use current sortConfig from table; fall back to count descending
  const activeSortConfig = sortConfig && sortConfig.key
    ? sortConfig
    : { key: `${crimePrefix}_count`, direction: 'desc' };

  const sorted = sortData(locations, activeSortConfig);
  const isMetroLt = locType === 'metro';
  const populationGroup = ctx.populationGroup ?? 'all';
  let working = sorted;
  if (tab === 'rate' && (isCountyOrCity || isMetroLt) && populationGroup !== 'all') {
    working = sorted.filter((loc) =>
      checkPopulationMatchExport(loc.population, populationGroup),
    );
  }
  const pageRows = working.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const sortedByLine = buildLocationTypeSortedByLine(
    ctx,
    sortApiTab,
    locType,
    isCountyOrCity,
    stateAbbr,
    activeSortConfig,
  );
  const paginationSummary = buildPaginationResultsLine(
    working.length,
    currentPage,
    itemsPerPage,
  );
  const paginationLineParts = getPaginationLineParts(working.length, currentPage, itemsPerPage);

  const nameOf = (loc) => buildLocTypeName(loc, locType);
  const rows   = [];

  if (tab === 'total') {
    const rankF    = isCountyOrCity
      ? (compareMode === 'state' ? `${crimePrefix}_state_count_rank` : `${crimePrefix}_national_count_rank`)
      : `${crimePrefix}_count_rank`;
    const avgDiffF  = isCountyOrCity
      ? (compareMode === 'state' ? `${crimePrefix}_state_count_avg_diff` : `${crimePrefix}_national_count_avg_diff`)
      : `${crimePrefix}_national_count_avg_diff`;
    const avgDiffPF = isCountyOrCity
      ? (compareMode === 'state' ? `${crimePrefix}_state_count_avg_diff_pct` : `${crimePrefix}_national_count_avg_diff_pct`)
      : `${crimePrefix}_national_count_avg_diff_pct`;

    const headers = [
      stackedHeader(`${isCountyOrCity ? `${scopeLabel} ${locLabel}` : locLabel}`.trim(), 'Rank'),
      locLabel,
      stackedHeader('Total', crimeTableHeader),
      stackedHeader(`${scopeLabel} ${locLabel} Avg`, 'Difference'),
      stackedHeader(`% ${scopeLabel} ${locLabel} Avg`, 'Difference'),
    ];
    pageRows.forEach(loc => rows.push([
      rk(loc[rankF]), nameOf(loc), n(loc[`${crimePrefix}_count`]),
      d(loc[avgDiffF]), d(loc[avgDiffPF], true),
    ]));
    return {
      title, filename, headers, rows, mainTableTopHeading, sortedByLine, paginationSummary, paginationLineParts,
    };
  }

  if (tab === 'trend') {
    const rankF = isCountyOrCity
      ? (compareMode === 'state' ? `${crimePrefix}_state_trend_rank` : `${crimePrefix}_national_trend_rank`)
      : `${crimePrefix}_trend_rank`;
    const headers = [
      stackedHeader(`${isCountyOrCity ? `${scopeLabel} ${locLabel}` : locLabel}`.trim(), 'Rank'),
      locLabel,
      stackedHeader('Total', crimeTableHeader),
      stackedHeader('Previous', 'Total'),
      stackedHeader('Change from', 'Previous Total'),
      stackedHeader('% Change from', 'Previous Total'),
    ];
    pageRows.forEach(loc => rows.push([
      rk(loc[rankF]), nameOf(loc), n(loc[`${crimePrefix}_count`]),
      n(loc[`${crimePrefix}_count_prev`]),
      d(loc[`${crimePrefix}_count_prev_diff`]),
      d(loc[`${crimePrefix}_count_prev_diff_pct`], true),
    ]));
    return {
      title, filename, headers, rows, mainTableTopHeading, sortedByLine, paginationSummary, paginationLineParts,
    };
  }

  if (tab === 'rate') {
    const { diffF, diffPctF } = getRateFields(
      crimePrefix, locType === 'metro', isCountyOrCity, compareMode, rateViewMode,
    );
    const rankF = getLocationTypeRateRankField(
      crimePrefix,
      locType,
      isCountyOrCity,
      compareMode,
      rateViewMode,
      populationGroup,
    );
    const rateComp = getLocationTypeRateComparisonLabel(locType, locType === 'metro', isCountyOrCity, stateAbbr, rateViewMode);
    const rateBase = (isCountyOrCity || locType === 'metro') ? rateComp : 'National';
    const headers = [
      stackedHeader(isCountyOrCity ? rateComp : locLabel, 'Rank'),
      locLabel,
      stackedHeader(locLabel, 'Population'),
      stackedHeader(crimeTableHeader, 'Rate'),
      stackedHeader(`${rateBase} Rate`, 'Difference'),
      stackedHeader(`% ${rateBase} Rate`, 'Difference'),
    ];
    pageRows.forEach(loc => rows.push([
      rk(loc[rankF]), nameOf(loc), n(loc.population), n(loc[`${crimePrefix}_rate`]),
      d(loc[diffF], false, true), d(loc[diffPctF], true),
    ]));
    return {
      title, filename, headers, rows, mainTableTopHeading, sortedByLine, paginationSummary, paginationLineParts,
    };
  }

  if (tab === 'score') {
    const scoreF = isCountyOrCity
      ? (compareMode === 'state' ? `${crimePrefix}_state_score` : `${crimePrefix}_national_score`)
      : `${crimePrefix}_score`;
    const rankF  = isCountyOrCity
      ? (compareMode === 'state' ? `${crimePrefix}_state_score_rank` : `${crimePrefix}_national_score_rank`)
      : `${crimePrefix}_score_rank`;
    const compScoreF = isCountyOrCity
      ? (compareMode === 'state' ? 'state_composite_score' : 'national_composite_score')
      : 'composite_score';
    const compositeScoreRankF = isCountyOrCity
      ? (compareMode === 'state' ? 'state_composite_score_rank' : 'national_composite_score_rank')
      : 'composite_score_rank';

    const scopeFirst = isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : '';
    const headers = [
      stackedHeader(`${isCountyOrCity ? `${scopeFirst} ${locLabel}` : locLabel}`.trim(), 'Rank'),
      locLabel,
      stackedHeader(`${scopeFirst} KRIME Score™`.trim(), '(Composite)'),
      stackedHeader(
        `${scopeFirst} ${selectedCrime === 'all_crime' ? 'All ' : ''}${crimeTableHeader}`.replace(/\s+/g, ' ').trim(),
        'Score (Non-Weighted)',
      ),
      stackedHeader(`${scopeFirst} ${locLabel} Rank`.trim(), '(Non-Weighted)'),
    ];
    pageRows.forEach(loc => rows.push([
      rk(loc[compositeScoreRankF]), nameOf(loc), n(loc[compScoreF], true),
      n(loc[scoreF], true), rk(loc[rankF]),
    ]));
    return {
      title, filename, headers, rows, mainTableTopHeading, sortedByLine, paginationSummary, paginationLineParts,
    };
  }

  return {
    title, filename, headers: [], rows: [], mainTableTopHeading, sortedByLine, paginationSummary, paginationLineParts,
  };
}

// ─── builder: LocationName (current year) ────────────────────────────────────
function buildLocationName(
  ctx, title, filename,
  locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr,
) {
  const { locationData, selectedSource, compareMode, rateViewMode } = ctx;
  const current  = locationData?.data?.current || {};
  const locLabel = locType.charAt(0).toUpperCase() + locType.slice(1);
  const scopeLabel = isCountyOrCity
    ? (compareMode === 'state' ? stateAbbr : 'US')
    : '';
  const rawTab = normalizeExportTab(ctx.activeTab);
  const effectiveTab = rawTab === 'report' ? 'total' : rawTab;
  const mainTableTopHeading = buildMainTableTopLocationName(ctx, effectiveTab, {
    locType, isNation, isCountyOrCity, isMetro, isNCVS, stateAbbr,
  });
  const rows = [];

  if (effectiveTab === 'total') {
    const headers = ['Type of Crime'];
    if (isNation) {
      headers.push(selectedSource === 'BJS_NCVS' ? 'Total Victimizations' : 'Total Crime');
    } else {
      headers.push(stackedHeader('Total', selectedSource === 'BJS_NCVS' ? 'Victimizations' : 'Crime'));
      headers.push(
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Average'),
        stackedHeader(`${scopeLabel} ${locLabel} Avg`, 'Difference'),
        stackedHeader(`% ${scopeLabel} ${locLabel} Avg`, 'Difference'),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Rank'),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Percentile'),
      );
    }
    crimeRows(isNCVS).forEach(({ crimeType, prefix }) => {
      const rankF = isCountyOrCity
        ? (compareMode === 'state' ? `${prefix}_state_count_rank` : `${prefix}_national_count_rank`)
        : `${prefix}_count_rank`;
      const pctF  = isCountyOrCity
        ? (compareMode === 'state' ? `${prefix}_state_count_percentile` : `${prefix}_national_count_percentile`)
        : `${prefix}_count_percentile`;
      const avgV  = compareMode === 'state' && isCountyOrCity
        ? current[`${prefix}_state_count_avg`] : current[`${prefix}_national_count_avg`];
      const dV    = compareMode === 'state' && isCountyOrCity
        ? current[`${prefix}_state_count_avg_diff`] : current[`${prefix}_national_count_avg_diff`];
      const dpV   = compareMode === 'state' && isCountyOrCity
        ? current[`${prefix}_state_count_avg_diff_pct`] : current[`${prefix}_national_count_avg_diff_pct`];
      const row   = [crimeType, n(current[`${prefix}_count`])];
      if (!isNation) row.push(n(avgV), d(dV), d(dpV, true), rk(current[rankF]), pc(current[pctF]));
      rows.push(row);
    });
    return { title, filename, headers, rows, mainTableTopHeading };
  }

  if (effectiveTab === 'trend') {
    const headers = [
      'Type of Crime',
      stackedHeader('Total', selectedSource === 'BJS_NCVS' ? 'Victimizations' : 'Crime'),
      stackedHeader('Previous', 'Total'),
      stackedHeader('Change from', 'Previous Total'),
      stackedHeader('% Change from', 'Previous Total'),
    ];
    if (!isNation) {
      headers.push(
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Rank'),
        stackedHeader(`${scopeLabel} ${locLabel}`, 'Percentile'),
      );
    }
    crimeRows(isNCVS).forEach(({ crimeType, prefix }) => {
      const rankF = isCountyOrCity
        ? (compareMode === 'state' ? `${prefix}_state_trend_rank` : `${prefix}_national_trend_rank`)
        : `${prefix}_trend_rank`;
      const pctF  = isCountyOrCity
        ? (compareMode === 'state' ? `${prefix}_state_trend_percentile` : `${prefix}_national_trend_percentile`)
        : `${prefix}_trend_percentile`;
      const row = [
        crimeType, n(current[`${prefix}_count`]), n(current[`${prefix}_count_prev`]),
        d(current[`${prefix}_count_prev_diff`]), d(current[`${prefix}_count_prev_diff_pct`], true),
      ];
      if (!isNation) row.push(rk(current[rankF]), pc(current[pctF]));
      rows.push(row);
    });
    return { title, filename, headers, rows, mainTableTopHeading };
  }

  if (effectiveTab === 'rate') {
    const hComp = getRatesComparisonLabelFromCompareMode(
      compareMode, isMetro, isCountyOrCity, stateAbbr, rateViewMode, locLabel,
    );
    const rankHeaderBase = isCountyOrCity ? hComp : locLabel;
    const headers = ['Type of Crime'];
    if (isNation && isNCVS) {
      headers.push('Victimization Rate');
    } else if (isNation) {
      headers.push('Crime Rate');
    } else {
      headers.push(
        stackedHeader('Crime', 'Rate'),
        stackedHeader(hComp, 'Rate'),
        stackedHeader(`${hComp} Rate`, 'Difference'),
        stackedHeader(`% ${hComp} Rate`, 'Difference'),
        stackedHeader(rankHeaderBase, 'Rank'),
        stackedHeader(rankHeaderBase, 'Percentile'),
      );
    }
    crimeRows(isNCVS).forEach(({ crimeType, prefix }) => {
      const { avgF, rankF, diffF, diffPctF, pctF } = getRateFields(
        prefix, isMetro, isCountyOrCity, compareMode, rateViewMode,
      );
      const row = [crimeType, n(current[`${prefix}_rate`])];
      if (!isNation) row.push(
        n(current[avgF]), d(current[diffF], false, true), d(current[diffPctF], true),
        rk(current[rankF]), pc(current[pctF]),
      );
      rows.push(row);
    });
    return { title, filename, headers, rows, mainTableTopHeading };
  }

  if (effectiveTab === 'score') {
    const scopeFirst = isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : '';
    const headers = [
      'Type of Crime',
      stackedHeader(`${scopeFirst} Crime Score`.trim(), '(Non-Weighted)'),
      stackedHeader(`${scopeFirst} ${locLabel}`.trim(), 'Rank'),
      stackedHeader('KRIME Score™', 'Weight'),
    ];
    crimeRows(false).forEach(({ crimeType, prefix }) => {
      const scoreF = isCountyOrCity
        ? (compareMode === 'state' ? `${prefix}_state_score` : `${prefix}_national_score`)
        : `${prefix}_score`;
      const rankF  = isCountyOrCity
        ? (compareMode === 'state' ? `${prefix}_state_score_rank` : `${prefix}_national_score_rank`)
        : `${prefix}_score_rank`;
      const weightKey = crimeType.toLowerCase().replace(/[\s/]/g, '_');
      const weightBase = weightedPercentages[weightKey] || '-';
      const weight = crimeTypesWithScoreWeightAsterisk(prefix) ? `${weightBase}*` : weightBase;
      rows.push([crimeType, n(current[scoreF], true), rk(current[rankF]), weight]);
    });
    return { title, filename, headers, rows, mainTableTopHeading };
  }

  return { title, filename, headers: [], rows: [], mainTableTopHeading };
}

// ─── rate field helpers ───────────────────────────────────────────────────────

/** Rank field for location-type rates list; must match LocationTypeRatesTable (incl. population filter). */
function getLocationTypeRateRankField(cp, locType, isCountyOrCity, compareMode, rateViewMode, populationGroup) {
  const pop = populationGroup ?? 'all';
  const suffix = pop === 'all' ? '' : `_${pop}`;
  const isMetro = locType === 'metro';

  if (isMetro) {
    return `${cp}_national_rate_rank${suffix}`;
  }
  if (isCountyOrCity) {
    const rt = rateViewMode?.rateType;
    const rs = rateViewMode?.rateScope || compareMode || 'state';
    if (rt === 'county' || rt === 'city') {
      if (rs === 'state') {
        return `${cp}_state_rate_rank${suffix}`;
      }
      return `${cp}_national_rate_rank${suffix}`;
    }
    if (rs === 'state') {
      return `${cp}_state_rate_rank${suffix}`;
    }
    return `${cp}_national_rate_rank${suffix}`;
  }
  return `${cp}_rate_rank`;
}

function getRateFields(cp, isMetro, isCountyOrCity, compareMode, rateViewMode) {
  if (isMetro) {
    const useMtr = rateViewMode?.rateType === 'metro';
    return {
      avgF:     useMtr ? `${cp}_all_metros_rate`          : `${cp}_national_rate`,
      diffF:    useMtr ? `${cp}_all_metros_rate_diff`      : `${cp}_national_rate_diff`,
      diffPctF: useMtr ? `${cp}_all_metros_rate_diff_pct`  : `${cp}_national_rate_diff_pct`,
      rankF:    `${cp}_national_rate_rank`,
      pctF:     `${cp}_national_rate_percentile`,
    };
  }
  if (isCountyOrCity) {
    const rt  = rateViewMode?.rateType;
    const rs  = rateViewMode?.rateScope || compareMode || 'state';
    const isLocType = rt === 'county' || rt === 'city';
    if (isLocType) {
      const pl    = rt === 'county' ? 'counties' : 'cities';
      const scope = rs === 'state';
      return {
        avgF:     scope ? `${cp}_all_${pl}_state_rate`          : `${cp}_all_${pl}_national_rate`,
        diffF:    scope ? `${cp}_all_${pl}_state_rate_diff`      : `${cp}_all_${pl}_national_rate_diff`,
        diffPctF: scope ? `${cp}_all_${pl}_state_rate_diff_pct`  : `${cp}_all_${pl}_national_rate_diff_pct`,
        rankF:    scope ? `${cp}_state_rate_rank`     : `${cp}_national_rate_rank`,
        pctF:     scope ? `${cp}_state_rate_percentile` : `${cp}_national_rate_percentile`,
      };
    }
    const scope = rs === 'state';
    return {
      avgF:     scope ? `${cp}_state_rate`           : `${cp}_national_rate`,
      diffF:    scope ? `${cp}_state_rate_diff`       : `${cp}_national_rate_diff`,
      diffPctF: scope ? `${cp}_state_rate_diff_pct`   : `${cp}_national_rate_diff_pct`,
      rankF:    scope ? `${cp}_state_rate_rank`       : `${cp}_national_rate_rank`,
      pctF:     scope ? `${cp}_state_rate_percentile` : `${cp}_national_rate_percentile`,
    };
  }
  return {
    avgF:     `${cp}_national_rate`,
    diffF:    `${cp}_national_rate_diff`,
    diffPctF: `${cp}_national_rate_diff_pct`,
    rankF:    `${cp}_rate_rank`,
    pctF:     `${cp}_rate_percentile`,
  };
}

function getRateCompLabel(isMetro, isCountyOrCity, compareMode, rateViewMode, stateAbbr, locLabel) {
  if (isMetro) return rateViewMode?.rateType === 'metro' ? 'Metro' : 'National';
  if (isCountyOrCity) {
    const rt = rateViewMode?.rateType;
    const rs = rateViewMode?.rateScope || compareMode || 'state';
    if (rt === 'county' || rt === 'city') {
      return rs === 'state'
        ? `${stateAbbr} ${rt === 'county' ? 'County' : 'City'}`
        : 'US National';
    }
    return rs === 'state' ? `${stateAbbr} State` : 'US National';
  }
  return 'National';
}
